/**
 * A mark per card, and the colour it is read in.
 *
 * Inline SVG on a 24-unit grid stroked with `currentColor`, like the funnel's set — drawn here
 * rather than pulled from a package because this is the screen people leave open all day, and an
 * icon font is a second request before it is usable.
 *
 * The tint is the figure's own meaning: money still in play is neutral, a projection is blue, money
 * won is green, something overdue is red. Four identical grey marks would be decoration, and a
 * green tick on a projection would be a lie. Every mark sits beside its label, so each is
 * `aria-hidden` and none is the only way to read the card.
 */

export type Tone = 'neutral' | 'info' | 'positive' | 'warning' | 'danger';

function Glyph({
  children,
  className = 'board-icon',
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

/** Leads: people who arrived. */
function LeadsGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19c0-3 2.7-5 6-5s6 2 6 5" />
      <path d="M16 6.5a3 3 0 0 1 0 5M17.5 14c2 .6 3.5 2.2 3.5 5" />
    </Glyph>
  );
}

/** Customers: a building, because a client here is an account rather than a person. */
function CompanyGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16" />
      <path d="M14 10h5a1 1 0 0 1 1 1v10M4 21h16" />
      <path d="M7 8h4M7 12h4M7 16h4M17 14h.01M17 18h.01" />
    </Glyph>
  );
}

/** The pipeline's worth: bars, because it is spread across stages. */
function PipelineGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M5 20V10M12 20V4M19 20v-7" />
    </Glyph>
  );
}

/** Won: a trophy. The one figure that is a fact rather than a projection. */
function TrophyGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M8 4h8v5a4 4 0 0 1-8 0z" />
      <path d="M8 6H5v1a3 3 0 0 0 3 3M16 6h3v1a3 3 0 0 1-3 3" />
      <path d="M12 13v4M9 20h6" />
    </Glyph>
  );
}

function TaskGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3v4M16 3v4" />
      <path d="m9 13 2 2 4-4" />
    </Glyph>
  );
}

function LateGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </Glyph>
  );
}

function ChatGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M20 14a2 2 0 0 1-2 2H8l-4 3V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
    </Glyph>
  );
}

/** Nobody has picked it up: a person, drawn once, with nothing beside them. */
function OrphanGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
    </Glyph>
  );
}

function SmsGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M7 11h10M7 14h6" />
    </Glyph>
  );
}

function SparkGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="m12 4 1.7 4.3L18 10l-4.3 1.7L12 16l-1.7-4.3L6 10l4.3-1.7z" />
      <path d="M18 16.5 18.8 18.2 20.5 19 18.8 19.8 18 21.5 17.2 19.8 15.5 19 17.2 18.2z" />
    </Glyph>
  );
}

function PercentGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="m6 18 12-12" />
      <circle cx="8" cy="8" r="2" />
      <circle cx="16" cy="16" r="2" />
    </Glyph>
  );
}

/** Anything the catalogue grows that nobody has drawn a mark for yet. */
function DefaultGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 15v-3M12 15V9M16 15v-5" />
    </Glyph>
  );
}

const MARKS: Record<string, { icon: () => React.JSX.Element; tone: Tone }> = {
  'crm.new_leads': { icon: LeadsGlyph, tone: 'info' },
  'crm.new_customers': { icon: CompanyGlyph, tone: 'neutral' },
  'sales.open_pipeline': { icon: PipelineGlyph, tone: 'info' },
  'sales.won_value': { icon: TrophyGlyph, tone: 'positive' },
  'sales.win_rate': { icon: PercentGlyph, tone: 'positive' },
  'work.open_tasks': { icon: TaskGlyph, tone: 'neutral' },
  'work.overdue_tasks': { icon: LateGlyph, tone: 'danger' },
  'inbox.open_conversations': { icon: ChatGlyph, tone: 'neutral' },
  'inbox.unassigned': { icon: OrphanGlyph, tone: 'neutral' },
  'marketing.sms_balance': { icon: SmsGlyph, tone: 'neutral' },
  'ai.spend': { icon: SparkGlyph, tone: 'info' },
};

/** The mark for a card, and the tint to read it in. Unknown keys get a neutral chart. */
export function markFor(key: string): { icon: React.JSX.Element; tone: Tone } {
  const mark = MARKS[key];

  return mark ? { icon: <mark.icon />, tone: mark.tone } : { icon: <DefaultGlyph />, tone: 'neutral' };
}

/** The calendar on the period control. */
export function RangeGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <rect x="4" y="6" width="16" height="14" rx="2" />
      <path d="M8 3v4M16 3v4M4 11h16" />
    </Glyph>
  );
}

/** The sliders on "Personalizar". */
export function TuneGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="17" r="2" />
    </Glyph>
  );
}

/** The plus on "Criar". */
export function PlusGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M12 5v14M5 12h14" />
    </Glyph>
  );
}

export function ChevronGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--xs">
      <path d="m6 9 6 6 6-6" />
    </Glyph>
  );
}

/** The arrow on "Ver funil" and the rest. */
export function GoGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <path d="M5 12h13M13 7l5 5-5 5" />
    </Glyph>
  );
}

/** Beside the count of campaigns. */
export function MailGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="m3.5 8 8.5 6 8.5-6" />
    </Glyph>
  );
}

/** Beside "Clientes por status". */
export function PeopleGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19c0-3 2.7-5 6-5s6 2 6 5" />
      <path d="M16 6.5a3 3 0 0 1 0 5M17.5 14c2 .6 3.5 2.2 3.5 5" />
    </Glyph>
  );
}

/** Beside "Origem dos clientes". */
export function TargetGlyph(): React.JSX.Element {
  return (
    <Glyph className="board-icon board-icon--sm">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" />
    </Glyph>
  );
}
